import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BlogsState, BlogPost, BlogComment, BlogLike } from '../types';

const initialState: BlogsState = {
  blogs: [],
  selectedBlog: null,
  featuredBlogs: [],
  categories: [],
  selectedCategory: null,
  searchQuery: '',
  loading: false,
  error: null,
  blogComments: {},
  blogLikes: {},
  userLikes: {},
};

const blogsSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {
    setBlogs: (state, action: PayloadAction<BlogPost[]>) => {
      state.blogs = action.payload;
      state.error = null;
    },
    addBlog: (state, action: PayloadAction<BlogPost>) => {
      state.blogs.unshift(action.payload);
    },
    updateBlog: (state, action: PayloadAction<BlogPost>) => {
      const index = state.blogs.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.blogs[index] = action.payload;
      }
      if (state.selectedBlog?.id === action.payload.id) {
        state.selectedBlog = action.payload;
      }
    },
    removeBlog: (state, action: PayloadAction<string>) => {
      state.blogs = state.blogs.filter((b) => b.id !== action.payload);
    },
    setSelectedBlog: (state, action: PayloadAction<BlogPost | null>) => {
      state.selectedBlog = action.payload;
    },
    setFeaturedBlogs: (state, action: PayloadAction<BlogPost[]>) => {
      state.featuredBlogs = action.payload;
    },
    setCategories: (state, action: PayloadAction<string[]>) => {
      state.categories = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategory = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setBlogComments: (state, action: PayloadAction<{ blogId: string; comments: BlogComment[] }>) => {
      state.blogComments[action.payload.blogId] = action.payload.comments;
    },
    addBlogComment: (state, action: PayloadAction<BlogComment>) => {
      const blogId = action.payload.blogId;
      if (!state.blogComments[blogId]) {
        state.blogComments[blogId] = [];
      }
      state.blogComments[blogId].push(action.payload);
      if (state.selectedBlog?.id === blogId) {
        state.selectedBlog.commentsCount = (state.selectedBlog.commentsCount || 0) + 1;
      }
    },
    setBlogLikes: (state, action: PayloadAction<{ blogId: string; likes: BlogLike[] }>) => {
      state.blogLikes[action.payload.blogId] = action.payload.likes;
    },
    addBlogLike: (state, action: PayloadAction<BlogLike>) => {
      const blogId = action.payload.blogId;
      if (!state.blogLikes[blogId]) {
        state.blogLikes[blogId] = [];
      }
      state.blogLikes[blogId].push(action.payload);
      state.userLikes[blogId] = true;
      if (state.selectedBlog?.id === blogId) {
        state.selectedBlog.likes = (state.selectedBlog.likes || 0) + 1;
      }
      const blog = state.blogs.find((b) => b.id === blogId);
      if (blog) {
        blog.likes = (blog.likes || 0) + 1;
      }
    },
    removeBlogLike: (state, action: PayloadAction<{ blogId: string; userId: string }>) => {
      const { blogId, userId } = action.payload;
      if (state.blogLikes[blogId]) {
        state.blogLikes[blogId] = state.blogLikes[blogId].filter((like) => like.userId !== userId);
      }
      state.userLikes[blogId] = false;
      if (state.selectedBlog?.id === blogId) {
        state.selectedBlog.likes = Math.max(0, (state.selectedBlog.likes || 0) - 1);
      }
      const blog = state.blogs.find((b) => b.id === blogId);
      if (blog) {
        blog.likes = Math.max(0, (blog.likes || 0) - 1);
      }
    },
    incrementBlogViews: (state, action: PayloadAction<string>) => {
      const blogId = action.payload;
      const blog = state.blogs.find((b) => b.id === blogId);
      if (blog) {
        blog.views = (blog.views || 0) + 1;
      }
      if (state.selectedBlog?.id === blogId) {
        state.selectedBlog.views = (state.selectedBlog.views || 0) + 1;
      }
    },
  },
});

export const {
  setBlogs,
  addBlog,
  updateBlog,
  removeBlog,
  setSelectedBlog,
  setFeaturedBlogs,
  setCategories,
  setSelectedCategory,
  setSearchQuery,
  setLoading,
  setError,
  clearError,
  setBlogComments,
  addBlogComment,
  setBlogLikes,
  addBlogLike,
  removeBlogLike,
  incrementBlogViews,
} = blogsSlice.actions;
export default blogsSlice.reducer;

